import { useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { videoKey } from "@app/hooks/videos";
import { VideoWizardContext } from "@app/views/projects/videoWizardContext";

// Builds the Zoltar prompt template for adjusting the campaign from the
// current video data plus the user's prompt. Passed to `EditPrompt` both as the
// submit template builder and for the "edit prompt template" preview.
export const getScriptPromptTemplate = (videoData, defaultTemplate) => {
  const currentCampaign =
    videoData?.campaigns?.[videoData.campaigns.length - 1];

  if (currentCampaign && Object.keys(currentCampaign).length > 0) {
    return defaultTemplate.toSpliced(0, 0, {
      role: "user",
      prompt_template: `Current data:
${JSON.stringify(currentCampaign)}`,
    });
  }

  return defaultTemplate;
};

const getSelectedCampaign = (videoData) =>
  videoData?.campaigns?.find(
    (campaign) => campaign.id === videoData?.selectedCampaignIds?.[0],
  );

/**
 * Editing the selected campaign from a wizard step: fine-tuning it through the
 * `EditPrompt`, editing a single field, and walking its history (undo/redo).
 *
 * @param {object} [options]
 * @param {boolean} [options.withScriptHistory] - Collapse the history to the
 *   campaigns whose `script` changed (the partnership-value flow); otherwise
 *   every campaign version is a history entry.
 * @param {string} [options.editScope] - The story section the step edits
 *   (`[Edit-scope]` of the prompt), which an edit targets by default.
 * @param {(previous: object, next: object) => void} [options.onEdited] -
 *   Called with the selected campaign before and after a successful edit.
 */
export const useCampaignEditing = ({
  withScriptHistory = false,
  editScope,
  onEdited,
} = {}) => {
  const { video, selectedCampaign, updateVideo, customerBranding } =
    useContext(VideoWizardContext);
  const queryClient = useQueryClient();

  const historicalCampaigns = withScriptHistory
    ? video.data?.campaigns
        ?.filter((c) => !!c?.script)
        .filter(
          (c, idx, arr) =>
            idx === arr.length - 1 || c.script !== arr[idx + 1].script,
        )
    : video.data?.campaigns;
  const hasHistoricalCampaigns = historicalCampaigns?.length > 1;
  const currentCampaignIndex = historicalCampaigns?.findIndex(
    (c) => c.id === selectedCampaign?.id,
  );
  const hasPreviousCampaign = currentCampaignIndex > 0;
  const hasNextCampaign =
    currentCampaignIndex < historicalCampaigns?.length - 1;

  // Reports the selected campaign before and after an edit, read from the
  // cache once the edited campaign has been persisted and selected.
  const notifyEdited = (previousCampaign) => {
    const nextCampaign = getSelectedCampaign(
      queryClient.getQueryData(videoKey.detail(video.data?.id)),
    );

    if (previousCampaign && nextCampaign) {
      onEdited?.(previousCampaign, nextCampaign);
    }
  };

  const handleSubmit = (prompt, setPrompt, videoData) => {
    const data = videoData ?? video.data;
    const previousCampaign = getSelectedCampaign(data);

    return customerBranding.refetch(
      {
        ...data,
        hint: prompt,
        // Only an edit of an existing campaign has a section to target — not a
        // regeneration from scratch.
        editScope: prompt && previousCampaign ? editScope : undefined,
      },
      null,
      {
        onSuccess: () => {
          setPrompt?.("");
          notifyEdited(previousCampaign);
        },
      },
    );
  };

  // Awaitable `handleSubmit`: resolves once the adjusted campaign has been
  // persisted and selected, rejects when the generation fails. `options`
  // overrides the step's `editScope`.
  const handleSubmitAsync = async (prompt, videoData, options = {}) => {
    const data = videoData ?? video.data;
    const previousCampaign = getSelectedCampaign(data);

    const result = await customerBranding.refetchAsync(
      {
        ...data,
        hint: prompt,
        editScope: options.editScope ?? editScope,
      },
      null,
    );

    notifyEdited(previousCampaign);

    return result;
  };

  const handleUndo = () => {
    const previousCampaignIndex =
      historicalCampaigns?.findIndex((c) => c.id === selectedCampaign?.id) - 1;
    if (previousCampaignIndex >= 0) {
      const previousCampaign = historicalCampaigns?.[previousCampaignIndex];
      updateVideo.mutate({
        selectedCampaignIds: [previousCampaign.id],
      });
    }
  };

  const handleRedo = () => {
    const nextCampaignIndex =
      historicalCampaigns?.findIndex((c) => c.id === selectedCampaign?.id) + 1;
    if (nextCampaignIndex < historicalCampaigns?.length) {
      const nextCampaign = historicalCampaigns?.[nextCampaignIndex];
      updateVideo.mutate({
        selectedCampaignIds: [nextCampaign.id],
      });
    }
  };

  const handleFieldChange = (fieldKey, value) => {
    if (!fieldKey || value === undefined) {
      return;
    }

    if (selectedCampaign?.[fieldKey] === value) {
      return;
    }

    const updatedCampaigns = [...video.data?.campaigns];
    const id = crypto.randomUUID();
    const updatedCampaign = {
      ...selectedCampaign,
      id,
      scenes: [],
      [fieldKey]: value,
    };

    updatedCampaigns.push(updatedCampaign);

    updateVideo.mutate({
      campaigns: updatedCampaigns.slice(-10),
      selectedCampaignIds: [id],
    });
  };

  return {
    historicalCampaigns,
    hasHistoricalCampaigns,
    hasPreviousCampaign,
    hasNextCampaign,
    handleSubmit,
    handleSubmitAsync,
    handleUndo,
    handleRedo,
    handleFieldChange,
  };
};
